/* Two services on one visit, done at once by two technicians.
 *
 * A client booked for a manicure at noon and a pedicure at quarter to one does
 * not have to be done one after the other. With a second technician free, the
 * pedicure can start any time from noon — her feet are in the tub while her
 * hands are being done. So a colleague free from noon to half past twelve is
 * free for that pedicure, even though the book has it at 12:45, and the board
 * should say so when it looks for somebody to take it.
 *
 * Not every pair works. Kevin, 2026-10-19: a pedicure can be done at the same
 * time as a manicure, a full set, a refill or a nail service; reflexology, a
 * head spa or a facial cannot be done alongside anything. So each turn type
 * says which side of the client it is (`sameTime`):
 *
 *   'feet'   pedicures
 *   'hands'  manicures, full sets, refills, nail services
 *   null     everything else: never alongside another service
 *
 * and two services may overlap only when one is feet and the other hands. Two
 * hand services never overlap — one client, one pair of hands.
 *
 * The exception is a visit whose two services are BOTH half turns. Those are
 * one technician's job, one after the other, the way a classic mani-pedi is
 * counted as two halves of one turn: they are never split across two people,
 * at the same time or otherwise. They can still go to somebody else together
 * (Kevin, 2026-10-19), which is what `halfPairs` is for.
 *
 * Pure: a service arrives as { id, from, to, side, weight, techs } with times
 * in ms, `side`/`weight` read off its turn type after options have had their
 * say, and `techs` who the book names on it.
 */

export var SIDES = ['feet', 'hands'];

// The stored value, or null for anything that is not one of the two sides.
export function cleanSide(value) {
  return value === 'feet' || value === 'hands' ? value : null;
}

function isHalf(s) { return Number(s && s.weight) === 0.5; }

/* Both half turns, and booked as one technician's job: with the same person,
 * or with nobody named on one of them. Two halves the book already has with two
 * different people — a removal with Thu and a classic pedicure with Ann — are
 * a split somebody has already made, not a pair. `techs` is who the book names
 * on each, in any form, compared as given; missing means nobody. */
export function halfPair(a, b) {
  if (!isHalf(a) || !isHalf(b)) return false;
  var x = (a && a.techs) || [], y = (b && b.techs) || [];
  if (!x.length || !y.length) return true;
  return x.some(function (t) { return y.indexOf(t) !== -1; });
}

/* May these two services on one visit run at the same time? */
export function mayOverlap(a, b) {
  if (!a || !b) return false;
  var x = cleanSide(a.side), y = cleanSide(b.side);
  if (!x || !y || x === y) return false;
  return !halfPair(a, b);
}

/* The earliest a colleague may start `target`, given the rest of its visit.
 *
 * Never before the visit's first service — the client is not there yet — and
 * never across a service of the visit that `target` may not run alongside: it
 * has to wait for that one to finish. A facial at eleven, a manicure at noon
 * and a pedicure at 12:45 lets the pedicure start from noon, not from eleven.
 * Only services booked before `target` count; nothing later can make it
 * earlier. Never later than the booked start. */
export function earliestStart(target, visit) {
  var list = visit || [];
  var e = target.from;
  list.forEach(function (q) { if (q.from < e) e = q.from; });
  list.forEach(function (q) {
    if (q.id === target.id || q.from >= target.from) return;
    if (!mayOverlap(target, q) && q.to > e) e = q.to;
  });
  return Math.min(e, target.from);
}

/* The starts worth trying between `earliest` and `latest`: the two ends, and
 * every moment something of the candidate's comes to an end in between (a
 * square, a booking), since those are the only places a free stretch can
 * begin. In order, without repeats. */
export function startsToTry(earliest, latest, ends) {
  var out = [earliest];
  (ends || []).forEach(function (t) {
    if (t > earliest && t < latest) out.push(t);
  });
  out.push(latest);
  out.sort(function (a, b) { return a - b; });
  return out.filter(function (t, i) { return i === 0 || t !== out[i - 1]; });
}

/* One technician's booked services grouped into what has to move together:
 * the half-turn services of one visit form a single unit, everything else is
 * on its own. `blocks` are { id, apptId, from, to, weight }, in any order;
 * units come back in order of their first start, each a list in start order. */
export function halfPairs(blocks) {
  var list = (blocks || []).slice().sort(function (a, b) { return a.from - b.from; });
  var byVisit = {};
  var units = [];
  list.forEach(function (b) {
    if (isHalf(b) && b.apptId != null) {
      var u = byVisit[b.apptId];
      if (u) { u.push(b); return; }
      u = byVisit[b.apptId] = [b];
      units.push(u);
      return;
    }
    units.push([b]);
  });
  return units;
}
